import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { eventsApi, inquiriesApi } from "../services/api";

// Master casino groups — same card number across all properties in a group
const CASINO_GROUPS = [
  { group: "MGM Resorts", properties: ["Bellagio", "ARIA", "Vdara", "MGM Grand", "The Signature", "Mandalay Bay", "Delano", "Park MGM", "NoMad", "New York-New York", "Excalibur", "Luxor", "The Mirage", "Borgata", "Beau Rivage", "MGM National Harbor", "MGM Springfield", "MGM Grand Detroit", "Empire City Casino"] },
  { group: "Caesars Entertainment", properties: ["Caesars Palace", "Paris Las Vegas", "Bally's Las Vegas", "Flamingo", "The LINQ", "The Cromwell", "Harrah's Las Vegas", "Rio All-Suite", "Planet Hollywood", "Horseshoe Las Vegas", "Harrah's Atlantic City", "Caesars Atlantic City", "Tropicana Atlantic City", "Harrah's New Orleans", "Horseshoe Bossier City", "Horseshoe Hammond", "Horseshoe Indianapolis", "Harrah's Cherokee", "Harrah's Ak-Chin", "Harrah's Laughlin", "Harrah's Lake Tahoe", "Harveys Lake Tahoe"] },
  { group: "Penn Entertainment", properties: ["Hollywood Casino Columbus", "Hollywood Casino Toledo", "Hollywood Casino Lawrenceburg", "Hollywood Casino at Charles Town", "Hollywood Casino Perryville", "Hollywood Casino Aurora", "Hollywood Casino Joliet", "Hollywood Casino St. Louis", "Hollywood Casino Tunica", "Hollywood Casino Gulf Coast", "Hollywood Casino at Penn National", "M Resort", "Tropicana Las Vegas", "Ameristar East Chicago", "Ameristar Council Bluffs", "Ameristar Kansas City", "Ameristar St. Charles", "L'Auberge Lake Charles", "L'Auberge Baton Rouge"] },
  { group: "Wynn Resorts", properties: ["Wynn Las Vegas", "Encore at Wynn"] },
  { group: "Las Vegas Sands / Venetian", properties: ["The Venetian Las Vegas", "The Palazzo"] },
  { group: "Hard Rock International", properties: ["Hard Rock Hotel & Casino Las Vegas", "Seminole Hard Rock Hollywood", "Seminole Hard Rock Tampa", "Hard Rock Atlantic City", "Hard Rock Cincinnati", "Hard Rock Northern Indiana", "Hard Rock Sacramento", "Hard Rock Biloxi"] },
  { group: "Boyd Gaming", properties: ["The Orleans", "Suncoast", "Gold Coast", "California Hotel", "Fremont", "Main Street Station", "Sam's Town Las Vegas", "Cannery Casino", "Aliante Casino", "IP Casino Biloxi", "Valley Forge Casino"] },
  { group: "Station Casinos", properties: ["Red Rock Casino", "Green Valley Ranch", "Palace Station", "Boulder Station", "Sunset Station"] },
  { group: "Mohegan Sun", properties: ["Mohegan Sun Connecticut", "Mohegan Sun Pocono", "Resorts Casino Hotel Atlantic City"] },
  { group: "Foxwoods Resort Casino", properties: ["Foxwoods Resort Casino"] },
  { group: "Genting Group", properties: ["Resorts World Las Vegas", "Resorts World New York City", "Resorts World Catskills"] },
  { group: "Wind Creek Hospitality", properties: ["Wind Creek Bethlehem", "Wind Creek Atmore", "Wind Creek Wetumpka"] },
  { group: "Cosmopolitan of Las Vegas", properties: ["The Cosmopolitan of Las Vegas"] },
  { group: "Circa Resort & Casino", properties: ["Circa Resort & Casino", "The D Las Vegas", "Golden Gate Hotel & Casino"] },
  { group: "Agua Caliente Casinos", properties: ["Agua Caliente Casino Palm Springs", "Agua Caliente Casino Rancho Mirage"] },
  { group: "Pechanga Resort Casino", properties: ["Pechanga Resort Casino"] },
  { group: "Yaamava' Resort & Casino", properties: ["Yaamava' Resort & Casino"] },
  { group: "Morongo Casino", properties: ["Morongo Casino Resort & Spa"] },
  { group: "FireKeepers Casino", properties: ["FireKeepers Casino Hotel"] },
  { group: "Atlantis Casino Resort", properties: ["Atlantis Casino Resort Spa Reno"] },
  { group: "Cruise Lines (Casino)", properties: ["Royal Caribbean Casino Royale", "Carnival Players Club", "Norwegian Cruise Line Casinos at Sea", "MSC Cruises Casino", "Celebrity Cruises Casino", "Princess Cruises Casino"] },
];

function EventDetail() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    currentCasino: "", currentCasinoOther: "", loyaltyTier: "", averagePlay: "",
    hostName: "", hostContact: "", offerDetails: "",
  });

  useEffect(() => {
    eventsApi.get(eventId)
      .then(setEvent)
      .catch(() => setEvent(getSampleEvent(eventId)))
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const submitData = { ...form, eventId };
      // Resolve "other" casino to the typed value
      if (submitData.currentCasino === "other" && submitData.currentCasinoOther) {
        submitData.currentCasino = submitData.currentCasinoOther;
      }
      delete submitData.currentCasinoOther;
      await inquiriesApi.submit(submitData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to submit inquiry. Please try again.");
    }
  };

  if (loading) return <div className="container" style={{ padding: "80px 0", color: "var(--text-light)" }}>Loading...</div>;
  if (!event) return <div className="container" style={{ padding: "80px 0" }}><p>Event not found. <Link to="/events">Back to events</Link></p></div>;

  return (
    <section className="event-detail container">
      <Link to="/events" className="back-link">← Back to Events</Link>

      <div style={{ marginBottom: "8px" }}>
        <span className="event-type">{event.eventType}</span>
      </div>
      <h1>{event.title}</h1>

      <div className="event-info">
        <div>
          <p className="event-description">{event.description}</p>

          {/* Image Gallery */}
          {event.images?.length > 0 && (
            <div className="event-gallery">
              <div className="event-gallery-main">
                <img src={event.images[0].url} alt={event.images[0].caption || event.title} />
              </div>
              {event.images.length > 1 && (
                <div className="event-gallery-thumbs">
                  {event.images.slice(1).map((img, idx) => (
                    <div key={idx} className="event-gallery-thumb">
                      <img src={img.url} alt={img.caption || `Photo ${idx + 2}`} />
                      {img.caption && <span className="event-gallery-caption">{img.caption}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* External Links */}
          {event.links?.length > 0 && (
            <div className="event-links">
              <h3>More Information</h3>
              <div className="event-links-list">
                {event.links.map((link, idx) => (
                  <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="event-link-card">
                    <span className="event-link-icon">🔗</span>
                    <span className="event-link-label">{link.label}</span>
                    <span className="event-link-arrow">→</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="form-section">
            {(() => {
              const eventExpired = event.status === "completed" || (event.startDate && new Date(event.startDate) < new Date());
              if (eventExpired) return (
                <div style={{ textAlign: "center", padding: "24px", color: "var(--text-light)" }}>
                  <p style={{ fontSize: "1.2rem", marginBottom: "6px" }}>🏁</p>
                  <p style={{ fontWeight: 600 }}>This event has ended</p>
                  <p style={{ fontSize: "0.88rem", marginTop: "4px" }}>Inquiries are no longer being accepted. Check out our other events.</p>
                </div>
              );
              if (submitted) return (
              <div className="alert alert-success">
                Your inquiry has been submitted. We'll review your information and get back to you within 24-48 hours.
              </div>
            );
            return (
              <>
                <h2>Submit Your Inquiry</h2>
                <p className="form-subtitle">
                  Tell us about your play history so we can check your qualification for this event.
                </p>
                {error && <div className="alert alert-error">{error}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input id="firstName" name="firstName" required value={form.firstName} onChange={handleChange} placeholder="John" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input id="lastName" name="lastName" required value={form.lastName} onChange={handleChange} placeholder="Doe" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="john@example.com" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(555) 123-4567" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="currentCasino">Current Casino / Property</label>
                      <select id="currentCasino" name="currentCasino" value={form.currentCasino} onChange={handleChange}>
                        <option value="">Select casino / property...</option>
                        {CASINO_GROUPS.map(g => (
                          <optgroup key={g.group} label={g.group}>
                            {g.properties.map(p => (
                              <option key={p} value={`${p} (${g.group})`}>{p}</option>
                            ))}
                          </optgroup>
                        ))}
                        <optgroup label="Other">
                          <option value="other">Other (specify below)</option>
                        </optgroup>
                      </select>
                      {form.currentCasino === "other" && (
                        <input name="currentCasinoOther" value={form.currentCasinoOther || ""} onChange={handleChange} placeholder="Type your casino name..." style={{ marginTop: "6px" }} />
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="loyaltyTier">Loyalty Tier Level</label>
                      <select id="loyaltyTier" name="loyaltyTier" value={form.loyaltyTier} onChange={handleChange}>
                        <option value="">Select tier...</option>
                        <option value="basic">Basic / Entry</option>
                        <option value="silver">Silver</option>
                        <option value="gold">Gold</option>
                        <option value="platinum">Platinum</option>
                        <option value="diamond">Diamond / VIP</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="averagePlay">Average Daily Play</label>
                      <select id="averagePlay" name="averagePlay" value={form.averagePlay} onChange={handleChange}>
                        <option value="">Select range...</option>
                        <option value="under-100">Under $100</option>
                        <option value="100-300">$100 – $300</option>
                        <option value="300-500">$300 – $500</option>
                        <option value="500-1000">$500 – $1,000</option>
                        <option value="1000-plus">$1,000+</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="hostName">Current Host Name</label>
                      <input id="hostName" name="hostName" value={form.hostName} onChange={handleChange} placeholder="Your casino host's name" />
                    </div>
                    <div className="form-group full">
                      <label htmlFor="offerDetails">Current Offers / Additional Details</label>
                      <textarea id="offerDetails" name="offerDetails" value={form.offerDetails} onChange={handleChange}
                        placeholder="Describe any current offers you've received, or any other details about your play history..." rows={4} />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ marginTop: "20px" }}>Submit Inquiry</button>
                </form>
              </>
            );
            })()}
          </div>
        </div>

        <aside className="event-sidebar">
          <h3>Event Details</h3>
          <div className="sidebar-item"><span className="label">Location</span><span className="value">{event.location}</span></div>
          <div className="sidebar-item"><span className="label">Start Date</span><span className="value">{event.startDate}</span></div>
          <div className="sidebar-item"><span className="label">End Date</span><span className="value">{event.endDate}</span></div>
          <div className="sidebar-item"><span className="label">Type</span><span className="value">{event.eventType}</span></div>
          <EventSidebarUrgency event={event} />
        </aside>
      </div>

      {/* Fine Print */}
      <div style={{ marginTop: "40px", padding: "24px", background: "var(--gray-50, #f9fafb)", borderRadius: "8px", borderTop: "1px solid var(--border-light, #e5e7eb)" }}>
        <h4 style={{ fontSize: "0.82rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-light, #6b7280)", marginBottom: "12px" }}>Fine Print</h4>
        <div style={{ fontSize: "0.78rem", lineHeight: 1.7, color: "var(--text-secondary, #6b7280)" }}>
          <p style={{ marginBottom: "10px" }}>
            Rated play from previous trips with this casino, any of the casino's affiliated brands, or from previous Comp'd Life trip(s) will initially be used to determine if a player qualifies for this offer. If there is no tracked play history with the casino or Comp'd Life to consider, qualification for this offer will be evaluated based on one or more of the following:
          </p>
          <ul style={{ paddingLeft: "20px", marginBottom: "10px" }}>
            <li>Images of complimentary cruise offers directly from the cruise line's casino department.</li>
            <li>Comp cruise certificates offered from land-based casinos. *An invitation to a certificate event at a casino can be accepted in lieu of the actual certificate.</li>
            <li>Images of complimentary room + free play offers from land-based casinos.</li>
            <li>Play history provided by player's casino host.</li>
          </ul>
          <p style={{ marginBottom: "10px" }}>Unless specified, offers cannot be combined with other offers.</p>
          <p style={{ marginBottom: "10px" }}>
            Comp'd Life can generally pre-qualify players based on offer images within 2 business days (and often within a couple of hours).
          </p>
          <p style={{ marginBottom: "10px" }}>
            Not everyone that requests an offer will qualify. The qualification criteria for this offer is set by the casino, not Comp'd Life. All requests are subject to final approval by the casino. All Comp'd Life offers are for players that Comp'd Life has booked at the property or cruise line in the past, or for new or inactive players to the specific casino partner. Players that are house-coded or active with the requested casino partner may be unable to receive this offer through Comp'd Life.
          </p>
          <p style={{ marginBottom: "10px" }}>All offers are non-transferable and based on availability, subject to change without notice.</p>
          <p>
            All complimentary offers are provided at the sole discretion of the casino or cruise line. Comp'd Life merely facilitates the provision of these offers and has no authority to issue or rescind them.
          </p>
        </div>
      </div>
    </section>
  );
}

function getSampleEvent(id) {
  const samples = {
    "sample-1": { eventId: "sample-1", title: "Caribbean Casino Cruise", description: "7-night luxury cruise with complimentary casino play, fine dining, and world-class entertainment. Ports of call include Cozumel, Grand Cayman, and Jamaica. Enjoy comp'd stateroom, meals, and onboard credits based on your play level. This is a premium experience designed for mid-level to high-level players looking for an unforgettable vacation.", eventType: "cruise", location: "Miami, FL", startDate: "2026-06-15", endDate: "2026-06-22", qualificationCriteria: "$500+ average daily play", minPlayLevel: "Mid-level", images: [{ url: "https://images.unsplash.com/photo-1548574505-5e239809ee19?w=800", caption: "Cruise ship at sea" }, { url: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600", caption: "Casino floor" }, { url: "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=600", caption: "Pool deck" }], links: [{ url: "https://example.com/itinerary", label: "Full Cruise Itinerary" }, { url: "https://example.com/faq", label: "Frequently Asked Questions" }] },
    "sample-2": { eventId: "sample-2", title: "Vegas VIP Resort Weekend", description: "All-inclusive weekend at a premier Las Vegas resort. Comp'd room, meals, show tickets, and tournament entry. Network with other players and meet casino hosts from top properties across the country.", eventType: "resort", location: "Las Vegas, NV", startDate: "2026-07-10", endDate: "2026-07-13", qualificationCriteria: "$300+ average daily play", minPlayLevel: "Entry-level", images: [{ url: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=800", caption: "Las Vegas strip" }, { url: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600", caption: "Luxury suite" }], links: [{ url: "https://example.com/vegas-resort", label: "Resort Details" }] },
    "sample-3": { eventId: "sample-3", title: "Atlantic City Flash Deal", description: "Last-minute comp'd stay at Borgata. 2 nights, meals, and $200 free play included. Perfect for players looking for a quick getaway with great value.", eventType: "short-notice", location: "Atlantic City, NJ", startDate: "2026-05-01", endDate: "2026-05-03", qualificationCriteria: "Any active player card", minPlayLevel: "Any", images: [], links: [] },
    "sample-4": { eventId: "sample-4", title: "High Roller Meetup — Biloxi", description: "Exclusive networking event for casino enthusiasts. Meet hosts from top Gulf Coast properties, learn about upcoming offers, and enjoy a comp'd dinner at a premier restaurant.", eventType: "meetup", location: "Biloxi, MS", startDate: "2026-08-20", endDate: "2026-08-20", qualificationCriteria: "Invitation or referral", minPlayLevel: "Mid-level", images: [], links: [] },
  };
  return samples[id] || null;
}

function EventSidebarUrgency({ event }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const capacity = event.maxCapacity || 0;
  const booked = event.totalHeadcount || event.currentInquiries || 0;
  const spotsLeft = capacity > 0 ? capacity - booked : null;
  const capacityPct = capacity > 0 ? (booked / capacity) * 100 : 0;

  const startDate = event.startDate ? new Date(event.startDate) : null;
  const diff = startDate ? startDate - now : null;
  const daysLeft = diff ? Math.ceil(diff / (1000 * 60 * 60 * 24)) : null;
  const showTimer = daysLeft !== null && daysLeft > 0 && daysLeft <= 14;

  const days = diff > 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) : 0;
  const hours = diff > 0 ? Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)) : 0;
  const minutes = diff > 0 ? Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)) : 0;
  const seconds = diff > 0 ? Math.floor((diff % (1000 * 60)) / 1000) : 0;

  if (!showTimer && spotsLeft === null) return null;

  return (
    <div style={{ marginTop: "16px", borderTop: "1px solid var(--border-light)", paddingTop: "16px" }}>
      {/* Countdown timer */}
      {showTimer && (
        <div style={{ marginBottom: capacity > 0 ? "14px" : 0 }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: daysLeft <= 3 ? "#EF4444" : "var(--text-light)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
            {daysLeft <= 3 ? "⏰ Closing Soon" : "⏳ Time Remaining"}
          </div>
          <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
            {[
              { val: days, label: "Days" },
              { val: hours, label: "Hrs" },
              { val: minutes, label: "Min" },
              { val: seconds, label: "Sec" },
            ].map(({ val, label }) => (
              <div key={label} style={{ textAlign: "center", flex: 1 }}>
                <div style={{
                  background: daysLeft <= 3 ? "#FEF2F2" : "var(--gray-100)",
                  borderRadius: "8px", padding: "8px 4px",
                  fontSize: "1.2rem", fontWeight: 800,
                  color: daysLeft <= 3 ? "#EF4444" : "var(--text)",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {String(val).padStart(2, "0")}
                </div>
                <div style={{ fontSize: "0.65rem", color: "var(--text-light)", marginTop: "3px", fontWeight: 600 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Capacity indicator */}
      {capacity > 0 && (
        <div>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: capacityPct >= 85 ? "#EF4444" : "var(--text-light)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>
            {capacityPct >= 85 ? "🔥 Almost Full" : "Availability"}
          </div>
          <div className="capacity-bar" style={{ marginBottom: "6px" }}>
            <div className="capacity-fill" style={{ width: `${Math.min(capacityPct, 100)}%`, background: capacityPct >= 85 ? "#EF4444" : capacityPct >= 60 ? "#F59E0B" : "#10B981" }} />
          </div>
          <div style={{ fontSize: "0.85rem", fontWeight: 600, textAlign: "center", color: spotsLeft <= 3 ? "#EF4444" : "var(--text-secondary)" }}>
            {spotsLeft > 0 ? `${spotsLeft} of ${capacity} spots remaining` : "Event is full"}
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetail;
